import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { selectAuthToken } from '../store/auth/selectors/auth.selectors';
import { JwtHelperService } from './jwt-helper.service';

/**
 * Super Admin Guard
 *
 * Protects routes that require ROLE_SUPER_ADMIN.
 * ROLE_ADMIN is NOT sufficient — this is a stricter gate than the
 * adminGuard, used for operations that touch external integrations,
 * OAuth tokens, bulk data imports, enrichment, GDPR consent, user
 * administration, whitelist, and user preferences.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'enrichment',
 *   canActivate: [superAdminGuard],
 *   component: EnrichmentComponent
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class SuperAdminGuardService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly jwtHelper = inject(JwtHelperService);

  canActivate(): Observable<boolean> {
    return this.store.select(selectAuthToken).pipe(
      map((token) => {
        if (!token?.accessToken) {
          console.warn('SuperAdminGuard: No access token found, redirecting to login');
          void this.router.navigate(['/login']);
          return false;
        }

        const isSuperAdmin = this.jwtHelper.hasRole(token.accessToken, 'ROLE_SUPER_ADMIN');

        if (!isSuperAdmin) {
          console.warn(
            'SuperAdminGuard: User does not have ROLE_SUPER_ADMIN, redirecting to dashboard',
          );
          void this.router.navigate(['/dashboard']);
          return false;
        }

        return true;
      }),
    );
  }
}

/**
 * Functional guard for use in route configuration.
 */
export const superAdminGuard = () => {
  return inject(SuperAdminGuardService).canActivate();
};
